import {
  ColumnConfigurationType,
  getConfigurationMetadata,
  hasDrawers,
} from '~/types/columnConfigurationTypes'
import { ColumnConfigurationWithOptions } from '~/types/furniture3D'
import { findNearestAvailableConfiguration } from './columnConfigurationFallback'
import { isConfigurationValid } from '~/config/columnConstraints'
import { isConfigurationValidForStand } from '~/config/columnConstraints.stand'
import { isConfigurationValidForTVStand } from '~/config/columnConstraints.tvstand'
import { isConfigurationValidForBedside } from '~/config/columnConstraints.bedside'
import { FurnitureProductType } from '~/hooks/useColumnConfigurationConstraints'

/**
 * Get the appropriate validation function based on product type
 */
function getValidationFunction(productType?: FurnitureProductType) {
  switch (productType) {
    case 'stand':
      return isConfigurationValidForStand
    case 'tv-stand':
      return isConfigurationValidForTVStand
    case 'bedside':
      return isConfigurationValidForBedside
    default:
      return isConfigurationValid
  }
}

/**
 * Maps drawer count to corresponding ColumnConfigurationType
 */
const DRAWER_TYPE_MAP: Record<number, ColumnConfigurationType> = {
  1: ColumnConfigurationType.DRAWERS_1,
  2: ColumnConfigurationType.DRAWERS_2,
  3: ColumnConfigurationType.DRAWERS_3,
  4: ColumnConfigurationType.DRAWERS_4,
  5: ColumnConfigurationType.DRAWERS_5,
}

/**
 * Maps section count to mixed single-door configuration type
 * Section count = shelfCount + 1 (1 drawer occupies 1 section)
 */
const MIXED_DOOR_TYPE_MAP: Record<number, ColumnConfigurationType> = {
  3: ColumnConfigurationType.DRAWER_AND_DOOR_2_SHELVES,
  4: ColumnConfigurationType.DRAWER_AND_DOOR_3_SHELVES,
  5: ColumnConfigurationType.DRAWER_AND_DOOR_4_SHELVES,
}

/**
 * Maps section count to mixed split-door configuration type
 */
const MIXED_SPLIT_DOOR_TYPE_MAP: Record<number, ColumnConfigurationType> = {
  3: ColumnConfigurationType.DRAWER_AND_DOOR_SPLIT_2_SHELVES,
  4: ColumnConfigurationType.DRAWER_AND_DOOR_SPLIT_3_SHELVES,
  5: ColumnConfigurationType.DRAWER_AND_DOOR_SPLIT_4_SHELVES,
}

/**
 * Returns the "section count" for a configuration, which is the universal
 * sync key across drawer-only and mixed configurations.
 * - Drawer-only: drawerCount (e.g. DRAWERS_3 → 3)
 * - Mixed (drawer + door): shelfCount + 1 (e.g. DRAWER_AND_DOOR_2_SHELVES → 3)
 */
export function getSectionCount(type: ColumnConfigurationType): number {
  const metadata = getConfigurationMetadata(type)
  if (metadata?.hasDrawers && metadata?.hasDoors) {
    return metadata.shelfCount + 1
  }
  return metadata?.drawerCount ?? 0
}

/**
 * Returns the target type for a given column when syncing to a new section count.
 * Preserves the column's "family" (drawer-only, mixed single-door, mixed split-door).
 */
function getTargetTypeForSync(
  currentType: ColumnConfigurationType,
  targetSectionCount: number
): ColumnConfigurationType | null {
  const metadata = getConfigurationMetadata(currentType)
  if (metadata?.hasDrawers && metadata?.hasDoors) {
    // Mixed config — pick from the right family
    if (metadata.doorCount === 2) {
      return MIXED_SPLIT_DOOR_TYPE_MAP[targetSectionCount] ?? null
    }
    return MIXED_DOOR_TYPE_MAP[targetSectionCount] ?? null
  }
  // Drawer-only
  return DRAWER_TYPE_MAP[targetSectionCount] ?? null
}

/**
 * Gets the section count from existing columns with drawers (drawer-only or mixed).
 * Returns the first found section count, or default (3) if none exist.
 */
function getTargetDrawerCountFromExisting(
  configurations: ColumnConfigurationWithOptions[],
  defaultCount: number = 3
): number {
  const existingSectionCounts = configurations
    .filter((config) => config && hasDrawers(config.type))
    .map((config) => getSectionCount(config!.type))

  return existingSectionCounts.length > 0
    ? existingSectionCounts[0]
    : defaultCount
}

/**
 * Creates configuration for an existing column, preserving it if valid,
 * or finding the nearest valid alternative
 */
export function createConfigurationForExistingColumn(
  existingConfig: ColumnConfigurationWithOptions,
  dimensions: { width: number; height: number; depth: number },
  productType?: FurnitureProductType,
  columnIndex?: number,
  totalColumns?: number
): ColumnConfigurationWithOptions {
  const isValid = getValidationFunction(productType)

  // If still valid, preserve the configuration
  if (isValid(existingConfig.type, dimensions)) {
    const metadata = getConfigurationMetadata(existingConfig.type)
    // If it's a single door and doorOpeningSide is not set, apply the default based on position
    if (
      metadata?.doorCount === 1 &&
      !existingConfig.doorOpeningSide &&
      columnIndex !== undefined &&
      totalColumns !== undefined
    ) {
      return {
        ...existingConfig,
        doorOpeningSide: getDefaultDoorOpeningSide(columnIndex, totalColumns),
      }
    }

    return existingConfig
  }

  // Get current drawer count to preserve it if possible
  const currentMetadata = getConfigurationMetadata(existingConfig.type)
  const currentDrawerCount = currentMetadata?.drawerCount

  // Find nearest valid alternative, preferring same drawer count
  const nearestType =
    findNearestAvailableConfiguration(
      existingConfig.type,
      dimensions,
      productType,
      currentDrawerCount
    ) || existingConfig.type

  const metadata = getConfigurationMetadata(nearestType)

  // Calculate default door opening side based on column position
  const doorOpeningSide =
    metadata?.doorCount === 1
      ? existingConfig.doorOpeningSide ||
        (columnIndex !== undefined && totalColumns !== undefined
          ? getDefaultDoorOpeningSide(columnIndex, totalColumns)
          : 'left')
      : undefined

  return { type: nearestType, doorOpeningSide }
}

/**
 * Creates configuration for a new column, syncing with existing drawer columns
 */
export function createConfigurationForNewColumn(
  existingConfigurations: ColumnConfigurationWithOptions[],
  dimensions: { width: number; height: number; depth: number },
  defaultDrawerCount: number = 3,
  productType?: FurnitureProductType,
  columnIndex?: number,
  totalColumns?: number
): ColumnConfigurationWithOptions {
  const targetDrawerCount = getTargetDrawerCountFromExisting(
    existingConfigurations,
    defaultDrawerCount
  )

  const defaultType =
    DRAWER_TYPE_MAP[targetDrawerCount] || ColumnConfigurationType.DRAWERS_3
  const nearestType =
    findNearestAvailableConfiguration(
      defaultType,
      dimensions,
      productType,
      targetDrawerCount
    ) || defaultType

  const metadata = getConfigurationMetadata(nearestType)

  // Calculate default door opening side based on column position for single door configurations
  const doorOpeningSide =
    metadata?.doorCount === 1 &&
    columnIndex !== undefined &&
    totalColumns !== undefined
      ? getDefaultDoorOpeningSide(columnIndex, totalColumns)
      : undefined

  return { type: nearestType, doorOpeningSide }
}

/**
 * Validates and updates invalid configurations when dimensions change
 * Preserves user's explicit doorOpeningSide choice, or applies position-based default
 */
export function validateAndUpdateConfigurations(
  configurations: ColumnConfigurationWithOptions[],
  dimensions: { width: number; height: number; depth: number },
  productType?: FurnitureProductType
): ColumnConfigurationWithOptions[] {
  const isValid = getValidationFunction(productType)

  const needsUpdate = configurations.some(
    (config) => !isValid(config.type, dimensions)
  )

  if (!needsUpdate) {
    return configurations
  }

  const totalColumns = configurations.length

  const result = configurations.map((config, columnIndex) => {
    const valid = isValid(config.type, dimensions)

    if (valid) {
      return config
    }

    // Get current drawer count to preserve it if possible
    const currentMetadata = getConfigurationMetadata(config.type)
    const currentDrawerCount = currentMetadata?.drawerCount

    const nearestType =
      findNearestAvailableConfiguration(
        config.type,
        dimensions,
        productType,
        currentDrawerCount
      ) || config.type

    const metadata = getConfigurationMetadata(nearestType)

    // Preserve user's choice, or use position-based default if not set
    const doorOpeningSide =
      metadata?.doorCount === 1
        ? config.doorOpeningSide ||
          getDefaultDoorOpeningSide(columnIndex, totalColumns)
        : undefined

    return { type: nearestType, doorOpeningSide }
  })

  return result
}

/**
 * Calculates the default door opening side based on column position
 * Rules:
 * - First column (index 0): 'left' opening side (handle on right)
 * - Last column: 'right' opening side (handle on left)
 * - All other columns: 'right' opening side (handle on left)
 */
export function getDefaultDoorOpeningSide(
  columnIndex: number,
  totalColumns: number
): 'left' | 'right' {
  if (columnIndex === 0) {
    // First column: handle on right = 'left' opening side
    return 'left'
  } else if (columnIndex === totalColumns - 1) {
    // Last column: handle on left = 'right' opening side
    return 'right'
  }

  // All middle columns: handle on left = 'right' opening side
  return 'right'
}

/**
 * Synchronizes section counts across all columns that have drawers
 * (both drawer-only and mixed drawer+door configurations).
 *
 * When a configuration is changed, all other columns with drawers are
 * updated to match the same section count while preserving their "family":
 *   - Drawer-only columns stay drawer-only (DRAWERS_3 → DRAWERS_4)
 *   - Mixed single-door columns stay mixed single-door
 *   - Mixed split-door columns stay mixed split-door
 *
 * Preserves user's explicit doorOpeningSide choice, or applies position-based default.
 */
export function synchronizeDrawerCounts(
  configurations: ColumnConfigurationWithOptions[],
  changedColumnIndex: number,
  newSectionCount: number,
  isValid: (type: ColumnConfigurationType) => boolean
): ColumnConfigurationWithOptions[] {
  const totalColumns = configurations.length

  return configurations.map((config, index) => {
    // Skip the column that was just changed
    if (index === changedColumnIndex) {
      return config
    }

    // Only update columns that have drawers (drawer-only or mixed)
    if (!config || !hasDrawers(config.type)) {
      return config
    }

    // Find the target type for this column's family
    const targetType = getTargetTypeForSync(config.type, newSectionCount)
    if (!targetType) {
      return config
    }

    // Only update if the target configuration is valid for this column
    if (!isValid(targetType)) {
      return config
    }

    const targetMetadata = getConfigurationMetadata(targetType)

    // Preserve user's choice, or use position-based default if not set
    const targetDoorOpeningSide =
      targetMetadata.doorCount === 1
        ? config.doorOpeningSide ||
          getDefaultDoorOpeningSide(index, totalColumns)
        : undefined

    return { type: targetType, doorOpeningSide: targetDoorOpeningSide }
  })
}
